import {
  CaretRightIcon,
  CheckIcon,
  PushPinIcon,
  ShieldSlashIcon,
  SparkleIcon,
} from '@phosphor-icons/react';
import { fmtMoneyWhole } from '../flight_format.js';
import type { FlightOfferSource } from '../flight_offer_source.js';
import { flightNumberLabel } from './flight_leg.js';
import { CAROUSEL_SLIDE } from './offer_carousel.js';
import type { FlightOfferCopy } from './offer_copy.js';
import { AirportSwitchTag, axisOf } from './offer_flags.js';
import type { FlightOfferRowProps, OfferPin } from './offer_row.js';
import { StackLeg, type StackLegText } from './stack_leg.js';

type OfferBadge = NonNullable<FlightOfferSource['badges']>[number];

/**
 * **O card de oferta do CELULAR (#3874)** — o desenho "Conversacional → Aéreo"
 * do Figma, nos tokens do DS. Mesmas props da linha desktop (`FlightOfferRow`
 * despacha para cá quando a lista mede menos que `sm`), outra árvore:
 *
 * - casca em duas peças: o CORPO (pernas + nota, cantos de cima) e a FAIXA DE
 *   PREÇO (cantos de baixo). A faixa inteira é o alvo — hoje ela abre a escada
 *   de tarifas inline, como o "Ver tarifas" do desktop; a tela cheia de seleção
 *   de tarifa é a #3875 e troca só o handler;
 * - selos sangrando a borda de cima, sem anel: o `padding-top` da lista dá o
 *   respiro. O pino da bancada (#2294) mora no MESMO trilho, canto direito, só
 *   glifo — o rodapé é a faixa de preço, e a linha "Detalhes" é por perna;
 * - por perna: cabeçalho `Ida · Sex, 25 set` com glifo, horários em
 *   `text-heading`, IATA em linha própria, condição/duração na coluna da
 *   conexão (saíram do cabeçalho), `+1` em `--danger-strong`.
 *
 * Tudo em utilitárias do DS: é um card novo sob `.genui2`, então nenhuma regra
 * nova entra no sheet legado (`flight_ui.css`). Só a expansão da escada
 * (`.fq-expansion`) segue de lá, porque é a mesma peça do desktop.
 */
export function FlightOfferCardStack<O extends FlightOfferSource>({
  slide,
  offer,
  position,
  onToggleFares,
  expanded,
  expansion,
  onOpenPolicy,
  onDetail,
  chosen,
  pin,
  variant = 'itinerary',
  airportSwitch,
  copy,
  locale,
  places,
}: FlightOfferRowProps<O>) {
  const text: StackLegText = { copy, locale, places };
  const topBadge = offer.badges?.[0];
  const out = offer.policy?.status === 'out';
  const results = variant === 'results';
  const bodyTap = bodyTapOf(offer, results ? onToggleFares : undefined);
  const detail = onDetail
    ? () => {
        onDetail(offer, position);
      }
    : undefined;
  return (
    <li
      // A elevação do bloco quando o card mora na thread (`--block-elevation`, #6296):
      // no `<li>` arredondado, porque o card são duas superfícies (corpo + faixa).
      className={`relative flex flex-col rounded-xl shadow-[var(--block-elevation,0_0_#0000)]${slide ? ` ${CAROUSEL_SLIDE}` : ''}`}
      data-offer-id={offer.offerId}
    >
      {topBadge && <StackAxisFlag badge={topBadge} copy={copy} />}
      {pin && <StackPin pin={pin} copy={copy} />}
      {out && (
        <StackPolicyFlag
          offer={offer}
          shifted={Boolean(pin)}
          copy={copy}
          onOpenPolicy={onOpenPolicy}
        />
      )}
      {/* `flex-1 content-start`: no carrossel os cards têm a MESMA altura (o
          `<li>` estica na linha), e é o corpo que cresce — a faixa de preço fica
          alinhada no pé de todos, e o texto do porquê pode quebrar à vontade. */}
      <div
        className={`grid flex-1 content-start rounded-t-xl border border-border bg-card text-ui text-foreground ${
          results ? 'cursor-pointer px-4 py-5' : 'p-4'
        }`}
        {...bodyTap}
      >
        {chosen && (
          <span className="mb-2 inline-flex items-center gap-1 justify-self-start text-detail font-medium text-muted-foreground">
            <CheckIcon size={12} aria-hidden />
            {copy.chosen}
          </span>
        )}
        <StackLegs offer={offer} variant={variant} onDetail={detail} text={text} />
        <AirportSwitchTag found={airportSwitch} copy={copy} />
        {offer.why && (
          <p className="m-0 flex gap-1 pt-1.5 text-ui text-foreground" title={offer.why}>
            <SparkleIcon size={16} weight="fill" className="mt-0.5 shrink-0" aria-hidden />
            <span className="line-clamp-2">{offer.why}</span>
          </p>
        )}
      </div>
      <PriceStrip
        offer={offer}
        position={position}
        expanded={expanded}
        caret={!results}
        onToggleFares={onToggleFares}
        copy={copy}
      />
      {expansion && (
        <div className="fq-expansion" role="presentation">
          {expansion}
        </div>
      )}
    </li>
  );
}

/** No resultado determinístico o card INTEIRO seleciona (#3893): o corpo
 *  repete o gesto da faixa, que segue sendo o alvo acessível (é ela que tem o
 *  nome e o foco). Os botões de dentro ("Detalhes") param a propagação. */
function bodyTapOf<O extends FlightOfferSource>(
  offer: O,
  onToggleFares: ((offer: O) => void) | undefined,
): { onClick?: () => void } {
  if (!onToggleFares) return {};
  return {
    onClick: () => {
      onToggleFares(offer);
    },
  };
}

/** As pernas do corpo: ida e volta com a régua entre elas, ou a perna única
 *  (que leva o nº do voo pronto, como no desktop — e, no cabeçalho da
 *  companhia, os números de TODOS os trechos, como o desenho da página). Só o
 *  card do chat abre a perna única pela data; ver `variant`. */
function StackLegs({
  offer,
  variant,
  onDetail,
  text,
}: {
  readonly offer: FlightOfferSource;
  readonly variant: NonNullable<FlightOfferRowProps['variant']>;
  readonly onDetail: (() => void) | undefined;
  readonly text: StackLegText;
}) {
  const segment = offer.segments[0];
  const returnSegment = offer.returnLeg?.segments[0];
  if (!segment) return null;
  const legHead = variant === 'chat' ? 'date' : 'carrier';
  const stops = offer.stops ?? offer.segments.length - 1;
  if (!offer.returnLeg || !returnSegment) {
    const flightNumber =
      legHead === 'carrier'
        ? offer.segments
            .map((s) => flightNumberLabel(s.carrier, s.flightNumber))
            .filter(Boolean)
            .join(', ')
        : flightNumberLabel(segment.carrier, segment.flightNumber);
    return (
      <StackLeg
        seg={segment}
        stops={stops}
        flightNumber={flightNumber}
        head={legHead}
        onDetail={onDetail}
        text={text}
      />
    );
  }
  return (
    <>
      <StackLeg seg={segment} stops={stops} kind="outbound" onDetail={onDetail} text={text} />
      {/* `-mx-4`: a régua vai de borda a borda do card, como no desenho. */}
      <hr className="-mx-4 my-4 border-0 border-t border-border" />
      <StackLeg
        seg={returnSegment}
        stops={offer.returnLeg.stops ?? offer.returnLeg.segments.length - 1}
        kind="return"
        onDetail={onDetail}
        text={text}
      />
    </>
  );
}

const STRIP =
  'flex items-center justify-end gap-1.5 rounded-b-xl border border-t-0 border-border bg-card p-4';

/**
 * A faixa de preço — "A partir de · valor · ›". A faixa INTEIRA é o alvo: na
 * lista do chat abre/fecha a escada (`aria-expanded`), na gaveta de filtro
 * seleciona o voo (sem `expanded`, o mesmo verbo do trilho desktop). Sem
 * `onToggleFares` (leitura) é só texto. O caret aponta para a direita porque a
 * promessa é uma TELA: a seleção de tarifa abre em tela cheia (#3875,
 * `OfferFaresDialog`), não embaixo do card. No resultado determinístico (#3893)
 * ele sai: ali o card inteiro é o alvo e o desenho é "A partir de · valor".
 */
function PriceStrip<O extends FlightOfferSource>({
  offer,
  position,
  expanded,
  caret,
  onToggleFares,
  copy,
}: Pick<FlightOfferRowProps<O>, 'offer' | 'position' | 'expanded' | 'onToggleFares'> & {
  readonly caret: boolean;
  readonly copy: FlightOfferCopy;
}) {
  const price = (
    <>
      <span className="text-ui text-muted-foreground">{copy.fromPrice}</span>
      <span className="text-heading text-foreground tabular-nums">
        {fmtMoneyWhole(offer.price)}
      </span>
    </>
  );
  if (!onToggleFares) return <div className={STRIP}>{price}</div>;
  return (
    <button
      type="button"
      className={`${STRIP} text-left transition-colors duration-[120ms] active:bg-accent motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring`}
      {...(expanded === undefined
        ? { 'aria-label': copy.selectOfferAria(position) }
        : { 'aria-expanded': expanded })}
      onClick={() => {
        onToggleFares(offer);
      }}
    >
      {price}
      {caret && <CaretRightIcon size={18} aria-hidden />}
    </button>
  );
}

const FLAG =
  'absolute -top-3 left-4 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-ui font-semibold';

/** O selo do eixo na gramática do celular: 14/20/600, glifo de 14 (Sparkle de 12),
 *  `--muted-foreground` para o recomendado e `--accent` para os outros. Cor
 *  continua não codificando o eixo — ver `AxisFlag`. */
function StackAxisFlag({
  badge,
  copy,
}: {
  readonly badge: OfferBadge;
  readonly copy: FlightOfferCopy;
}) {
  const spec = axisOf(badge.kind, copy);
  // Eixo sem desenho: rótulo do wire sem glifo — o Sparkle é do recomendado
  // (ver `AxisFlag`).
  if (!spec) {
    return <span className={`${FLAG} bg-accent text-foreground`}>{badge.label}</span>;
  }
  const Glyph = spec.Glyph;
  return (
    <span
      className={`${FLAG} ${spec.soft ? 'bg-accent text-foreground' : 'bg-muted-foreground text-background'}`}
    >
      <Glyph size={spec.soft ? 14 : 12} />
      {spec.text}
    </span>
  );
}

/** Fora da política: só o glifo, em `--destructive`, com o texto no
 *  `aria-label`. `shifted` abre espaço para o pino no mesmo canto. */
function StackPolicyFlag<O extends FlightOfferSource>({
  offer,
  shifted,
  copy,
  onOpenPolicy,
}: {
  readonly offer: O;
  readonly shifted: boolean;
  readonly copy: FlightOfferCopy;
  readonly onOpenPolicy?: (offer: O) => void;
}) {
  const className = `absolute -top-3 ${shifted ? 'right-12' : 'right-4'} inline-flex h-6 items-center rounded-full bg-destructive px-1.5 text-white`;
  const glyph = <ShieldSlashIcon size={14} aria-hidden />;
  if (!onOpenPolicy) {
    return (
      <span className={className} role="img" aria-label={copy.outPolicy}>
        {glyph}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`${className} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring`}
      aria-label={copy.outPolicyAria}
      onClick={() => {
        onOpenPolicy(offer);
      }}
    >
      {glyph}
    </button>
  );
}

/** O "Fixar" da bancada (#2294) no trilho dos selos: 24px, só glifo, o estado
 *  na tinta (fixado = ink invertido). A palavra vai no nome acessível, com
 *  `aria-pressed` carregando o estado, como no toggle do desktop. */
function StackPin({ pin, copy }: { readonly pin: OfferPin; readonly copy: FlightOfferCopy }) {
  return (
    <button
      type="button"
      className={`absolute -top-3 right-4 grid size-6 place-items-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${
        pin.pinned ? 'bg-foreground text-background' : 'bg-accent text-foreground'
      }`}
      aria-pressed={pin.pinned}
      aria-label={pin.pinned ? copy.pinOn : copy.pinOff}
      onClick={pin.onToggle}
    >
      <PushPinIcon size={14} weight={pin.pinned ? 'fill' : 'regular'} aria-hidden />
    </button>
  );
}
